/* The one semantic tone map (packages/ui/CLAUDE.md section C7, ADR-0092
 * amendment of 24 Sep 2026). Every part with a `tone` prop reads its colours
 * from this map, so one tone never means two colours. `accent` is not a tone:
 * it stays the one commit button of a region (invariant 9). Parts set the
 * border WIDTH themselves; these strings carry colour only. Literal class
 * strings so Tailwind sees them. */

export type StatusTone =
  | "healthy"
  | "watch"
  | "critical"
  | "neutral"
  | "info"
  | "ai";

export interface ToneClasses {
  /** A small filled mark: StatusDot's dot and its ping ring. */
  readonly dot: string;
  /** The dot colour as a border: StatusDot's outline and dashed marks. */
  readonly dotBorder: string;
  /** Tinted fill, tone border colour and tone text. */
  readonly soft: string;
  /** Saturated fill and its foreground. Neutral never saturates, so its
   *  solid is its soft. Every pair sits in tokens.json `$contrast.solidFills`. */
  readonly solid: string;
  /** The tone's border colour alone. */
  readonly border: string;
  /** The tone's text colour alone. */
  readonly text: string;
  /** Card's tone wash, under content that keeps its own text colours: the
   *  tone hue at `--pbds-opacity-tone-wash`, lighter than the soft fill so
   *  body, secondary and muted copy all read on it (tokens.json
   *  `$contrast.toneFills`). Neutral's is its opaque soft fill. */
  readonly fill: string;
}

export type ToneMap = Readonly<Record<StatusTone, ToneClasses>>;

export const TONES: ToneMap = {
  healthy: {
    dot: "bg-pb-green",
    dotBorder: "border-pb-green",
    soft: "border-pb-green/40 text-pb-green-text bg-pb-green-soft",
    solid: "bg-pb-green text-pb-on-accent",
    border: "border-pb-green/40",
    text: "text-pb-green-text",
    fill: "bg-pb-green/[calc(var(--pbds-opacity-tone-wash)*100%)]",
  },
  watch: {
    dot: "bg-pb-amber",
    dotBorder: "border-pb-amber",
    soft: "border-pb-amber/40 text-pb-amber-text bg-pb-amber-soft",
    // Not bg-pb-amber: on-accent is white in the light theme, and white on
    // pb-amber measures 2.9:1 there. The amber text ramp carries it in all
    // three themes (contrast:check).
    solid: "bg-pb-amber-text text-pb-on-accent",
    border: "border-pb-amber/40",
    text: "text-pb-amber-text",
    fill: "bg-pb-amber/[calc(var(--pbds-opacity-tone-wash)*100%)]",
  },
  critical: {
    dot: "bg-pb-red",
    dotBorder: "border-pb-red",
    soft: "border-pb-red/40 text-pb-red-text bg-pb-red-soft",
    solid: "bg-pb-red text-pb-on-accent",
    border: "border-pb-red/40",
    text: "text-pb-red-text",
    fill: "bg-pb-red/[calc(var(--pbds-opacity-tone-wash)*100%)]",
  },
  neutral: {
    dot: "bg-pb-text-muted",
    dotBorder: "border-pb-text-muted",
    soft: "bg-pb-card-elevated border-pb-border text-pb-text",
    solid: "bg-pb-card-elevated border-pb-border text-pb-text",
    border: "border-pb-border",
    text: "text-pb-text-muted",
    fill: "bg-pb-card-elevated",
  },
  info: {
    dot: "bg-pb-cyan",
    dotBorder: "border-pb-cyan",
    soft: "border-pb-cyan/40 text-pb-cyan-text bg-pb-cyan-soft",
    solid: "bg-pb-cyan text-pb-on-accent",
    border: "border-pb-cyan/40",
    text: "text-pb-cyan-text",
    fill: "bg-pb-cyan/[calc(var(--pbds-opacity-tone-wash)*100%)]",
  },
  ai: {
    dot: "bg-pb-iris",
    dotBorder: "border-pb-iris",
    soft: "border-pb-iris/40 text-pb-iris-text bg-pb-iris-soft",
    // The CTA trio (ADR-0076): the softer pb-iris fill does not carry copy.
    solid: "bg-pb-iris-interactive text-pb-primary-foreground",
    border: "border-pb-iris/40",
    text: "text-pb-iris-text",
    fill: "bg-pb-iris/[calc(var(--pbds-opacity-tone-wash)*100%)]",
  },
};
